/*
 * The shell for authenticated screens, and nothing for the rest.
 *
 * It reads ROUTES twice: once to find the matched entry (is this screen inside the shell at all)
 * and once to derive the nav. Both come from the manifest rather than from a list here, so a screen
 * cannot appear in navigation without being a registered route.
 */

import { useState } from 'react';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AppShell,
  Button,
  Card,
  FullBleed,
  Nav,
  PersonCapsule,
  Spinner,
  BrandMark,
} from '@indovation/design-system';
import type { NavGroup } from '@indovation/design-system';
import { SetName } from './set-name.js';
import { useI18n } from '@indovation/i18n/react';
import type { MessageKey } from '@indovation/i18n';
import { ROUTES } from '../routes.js';
import { NAV_SECTIONS } from '../route-manifest.js';
import type { NavSection } from '../route-manifest.js';
import { can, hasEntitlement, useSession } from '../auth/session.js';
import { memberById, personName } from '../people/people.js';
import ScopeSelector from '../scope/scope-selector.js';
import CopilotFlyout from '../copilot/flyout.js';
import { useSideSurface } from './side-surface.js';
import { useCopilotSession } from '../copilot/context.js';
import { useShortcut } from '../keyboard/shortcuts.js';
import { ThemeToggle } from '../theme/theme.js';
import TopSearch from './top-search.js';
import { NAV_ICONS } from './nav-icons.js';

/** The eyebrow for each section, in the order `NAV_SECTIONS` declares them. */
const SECTION_LABELS: Record<NavSection, MessageKey> = {
  recruiting: 'shell.nav.section.recruiting',
  insights: 'shell.nav.section.insights',
  administration: 'shell.nav.section.administration',
};


/** Renders the shell for authenticated screens and nothing for the rest. */
export default function RootLayout() {
  const { t, formatDateTime } = useI18n();
  const { status, me, api, signedInAt, signOut } = useSession();

  /*
   * ── The navigation column docks — 26 Aug 2026 ─────────────────────────────────────────────────
   *
   * *"Can the left panel hide to give more space… all the screen will get more space."*
   *
   * **`localStorage`, not `access.preference`, and that is a judgement rather than a shortcut.**
   * `copilot_pinned` is a server preference because wanting the assistant beside every record is a
   * fact about how somebody works, and it should follow them to any machine. This is a fact about
   * *the screen they are sitting at*: docked is right on a laptop and pointless on the 27-inch
   * monitor in the office, and a preference that followed them would arrive wrong half the time.
   * Same argument, opposite answer — see `useBandDensity`, which stores view density the same way
   * and for the same reason.
   *
   * Keyed by membership, so two people sharing a browser do not inherit each other's column.
   */
  const dockKey = me?.membership.id ? `tf.shell.nav.${me.membership.id}` : null;
  /*
   * Null until this session says otherwise, and what is stored decides until then — the shape
   * `useBandDensity` uses, and it is load-bearing rather than stylistic.
   *
   * A lazy `useState` initialiser was the obvious way to write this and it silently did not work:
   * the first render happens while the session is still resolving, so `me` is null, so the key is
   * null, so it read nothing and froze `false` — and the stored preference then never applied,
   * because an initialiser runs once. Reading on every render instead means the value arrives with
   * the session.
   */
  const [navChoice, setNavChoice] = useState<boolean | null>(null);
  const navRemembered = (() => {
    try {
      return dockKey !== null && window.localStorage.getItem(dockKey) === 'collapsed';
    } catch {
      // A browser configured to deny storage throws on read. Open is the right fallback: it is the
      // state that shows everything, and a nav nobody can find is worse than a narrow screen.
      return false;
    }
  })();
  const navCollapsed = navChoice ?? navRemembered;
  const toggleNav = () => {
    const next = !navCollapsed;
    setNavChoice(next);
    try {
      if (dockKey !== null) window.localStorage.setItem(dockKey, next ? 'collapsed' : 'open');
    } catch {
      // The column still docks; it simply does not survive the reload.
    }
  };

  /*
   * The signed-in person's own name, and why it comes from the team list.
   *
   * **`/v1/me` returns no display identity at all** — a tenant, a membership id, a role key, a
   * record scope and an entitlement list, but no name and not even an email address. It was built
   * to answer "what may this principal do", and until now nothing needed to answer "who is this".
   *
   * `access.member.list` already returns `full_name`, `email` and `is_self` for everyone in the
   * tenant, and `team.tsx` has found self through it since it was written. So this is the existing
   * pattern rather than a new read path, and it needs no contract change. Widening `/v1/me` would
   * be the tidier answer and is worth doing the day a second thing needs it; one caller does not
   * justify changing the shape every screen depends on.
   */
  const team = useQuery({
    queryKey: ['members'],
    queryFn: () => api.call('access.member.list'),
    enabled: status === 'authenticated',
  });

  /*
   * The copilot's two states, and why they are two things rather than one.
   *
   * `open` is this session — summoned with `[` or the header button, gone when the tab closes.
   * `pinned` is a preference, so somebody who wants the assistant docked beside every record gets
   * it back tomorrow without asking. Pinning implies open; closing a pinned flyout unpins it,
   * because a docked panel that is not showing is a setting with no visible effect.
   */
  const queryClient = useQueryClient();
  const copilot = useCopilotSession();

  const preferences = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.call('access.preference.list', {}),
    enabled: status === 'authenticated',
  });

  const pinned = preferences.data?.preferences?.copilot_pinned === true;

  const setPinned = useMutation({
    mutationFn: (next: boolean) =>
      api.call('access.preference.save', {
        params: { key: 'copilot_pinned' },
        body: { value: next },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['preferences'] }),
  });

  const showing = copilot.showing || pinned;

  /*
   * The assistant claims the edge only while it is actually on screen.
   *
   * Claiming unconditionally would make the register say the copilot holds the edge on every screen
   * of the app, and a flyout would then always be "displacing" something that was never there.
   */
  const holdsEdge = useSideSurface(showing ? 'copilot' : null);

  /*
   * Registered through the ordinary registry rather than as a private listener in the frame, so
   * there is one path from a key to its handler and no second place for a binding to hide.
   *
   * `[` toggles what is *visible*, which is the only thing a person pressing it is thinking about.
   * Closing a docked panel therefore undocks it, for the same reason the close button does: a
   * preference whose effect is hidden is a preference nobody can turn off.
   */
  useShortcut('copilot', () => {
    if (showing) {
      copilot.close();
      if (pinned) setPinned.mutate(false);
      return;
    }
    copilot.open();
  });

  const self = me ? memberById(team.data?.members ?? [], me.membership.id) : undefined;
  // The matched route's pattern, not the address bar. Every route here is currently static, so
  // comparing pathnames would still work — and would break silently on the first `$param` route,
  // by rendering that screen with no shell rather than by throwing. The admin console hit exactly
  // that on its tenant detail screen.
  // `String(...)` is load-bearing. `matches` is `RouteMatch<any, ...>`, so `routeId` is `any`,
  // and `useRouterState` resolves its result as `unknown extends TSelected ? RouterState : …` —
  // which `any` satisfies, handing back the entire router state instead of the selection.
  const routeId = useRouterState({
    select: (state) => String(state.matches.at(-1)?.routeId ?? ''),
  });

  const entry = ROUTES.find((candidate) => candidate.path === routeId);

  if (status === 'loading') {
    return (
      <FullBleed panelExempt="The session is still resolving; there is nothing to assist with yet.">
        <Card title={t('app.loading')}>
          <Spinner />
        </Card>
      </FullBleed>
    );
  }

  if (!entry?.authenticated || status !== 'authenticated') {
    return <Outlet />;
  }

  const navItems = ROUTES.filter(
    (candidate) =>
      candidate.nav &&
      hasEntitlement(me, candidate.entitlement) &&
      // A nav item for a screen the principal will be refused is worse than no nav item: it
      // teaches people that parts of the product are broken rather than not theirs.
      (!candidate.requires || can(me, candidate.requires.action, candidate.requires.resource)),
  ).sort((a, b) => (a.nav?.order ?? 0) - (b.nav?.order ?? 0));

  /*
   * The three sections, plus the headingless group above them — M-25.
   *
   * Built by walking `NAV_SECTIONS` rather than by grouping whatever the manifest happens to
   * contain, so the *order of the sections* is a declared fact in one place and cannot be changed
   * by adding a route. `order` inside a section is the manifest's, which is why the sort above
   * still runs first: this only partitions what that produced.
   *
   * A section whose items were all filtered out arrives here empty and `Nav` drops it — a tenant
   * without the analytics module sees no INSIGHTS heading rather than an empty one.
   */
  const groups: NavGroup[] = [
    {
      key: 'pinned',
      eyebrow: null,
      items: navItems
        .filter((candidate) => candidate.nav?.section === null)
        .map((candidate) => ({
          key: candidate.id,
          label: t(candidate.nav!.labelKey),
          href: candidate.path,
          icon: NAV_ICONS[candidate.id],
        })),
    },
    ...NAV_SECTIONS.map((section) => ({
      key: section,
      eyebrow: t(SECTION_LABELS[section]),
      items: navItems
        .filter((candidate) => candidate.nav?.section === section)
        .map((candidate) => ({
          key: candidate.id,
          label: t(candidate.nav!.labelKey),
          href: candidate.path,
          icon: NAV_ICONS[candidate.id],
        })),
    })),
  ];

  return (
    <AppShell
      labels={{
        nav: t('shell.nav.home'),
        copilot: t('app.name'),
        collapseNav: t('shell.nav.collapse'),
        expandNav: t('shell.nav.expand'),
      }}
      brand={<SetName text={t('app.name')} />}
      brandMark={<BrandMark label={t('brand.name')} scale={1.3} />}
      navCollapsed={navCollapsed}
      onToggleNav={toggleNav}
      header={
        <>
          <span>{me?.tenant.name}</span>
          {/*
            * CO2 §2.2's search, out of the sidebar at last — M-25.
            *
            * First in the header rather than last, because it is the only control here that
            * *takes* you somewhere; everything after it changes how the current screen reads.
            */}
          <TopSearch />
          {/* §4.3 puts the scope selector beside the tenant, and the order matters: tenant then
              scope reads as "which agency, then how much of it" — the two questions that decide
              what every number on the page means, narrowing left to right. */}
          <ScopeSelector />
          {/* After scope, before sign-out: it changes how the product looks and nothing about what
              the product is showing, so it belongs with the personal controls rather than with the
              two selectors that decide what every number on the page means. */}
          <ThemeToggle />
          {/*
            * §13's summon. Beside the theme toggle rather than in the nav, because it is a control
            * over the frame rather than a place to go — and `aria-pressed` rather than a second
            * label, so a screen reader hears one control with a state instead of two buttons that
            * swap.
            */}
          <Button
            size="sm"
            variant="ghost"
            aria-pressed={showing}
            onClick={() => (showing ? copilot.close() : copilot.open())}
          >
            {/*
              The assistant's own mark, which the kit gives as the two barred e's — the same shape
              it gives for "the agent's chip beside a message". Hidden from assistive technology
              because the button's text already names it; a mark announced beside the word it
              stands for is the same control read twice.
            */}
            <BrandMark className="tf-assistant-mark" />
            <SetName text={t('copilot.open')} />
          </Button>
          {/*
            * Who is signed in, and since when.
            *
            * The name falls back to the email address and then to the role, in that order, so the
            * capsule is never blank while the team list is still in flight — and never blank at
            * all for somebody whose account has no name yet.
            *
            * `formatDateTime` rather than a built string: FE-03 forbids literal prose in a render
            * path, and a hand-formatted clock would be wrong in every locale but one.
            */}
          <PersonCapsule
            /*
             * **The last resort is not a role, and it used to be — fixed 14 Aug 2026.**
             *
             * `personName` degrades name → email → this argument, and this argument was the role
             * label. So a capsule that could not find its own membership rendered "Administrator"
             * with the initial "A", and looked exactly like a person called Administrator. It is
             * the worst kind of fallback: one that fails into something plausible.
             *
             * The bug it disguised was real. The attack battery had been overwriting the demo
             * tenant, so `self` was genuinely missing, and the header said "Administrator" instead
             * of saying it did not know — which is why the environment looked odd rather than
             * broken. `dev/tenant-separation.test.ts` now stops the cause; this stops the disguise.
             */
            name={personName(self, t('shell.user.unknown'))}
            detail={
              signedInAt === null
                ? undefined
                : t('shell.user.signedInAt', { at: formatDateTime(new Date(signedInAt)) })
            }
            size="sm"
            facts={
              self
                ? [
                    { label: t('team.column.email'), value: self.email },
                    {
                      label: t('team.column.role'),
                      value: t(`team.role.${self.role_key}` as MessageKey),
                    },
                  ]
                : undefined
            }
          />
          <Button size="sm" variant="ghost" onClick={signOut}>
            {t('auth.signOut.action')}
          </Button>
        </>
      }
      nav={
        <Nav
          groups={groups}
          collapsed={navCollapsed}
          label={t('shell.nav.home')}
          /*
           * The router's `Link` supplies `aria-current="page"`, which is the whole of the
           * active-item styling — `.tf-nav__link[aria-current='page']`. Rendering it here rather
           * than in the design system is the arrangement `nav.tsx` explains: this app knows its
           * route tree, that package must not.
           */
          /*
            `content` rather than `item.label`: docked, the link holds a glyph and a hidden name,
            and that decision belongs to the component that knows which state it is in. This app
            still owns the routing, which is the whole of the arrangement `nav.tsx` describes.
          */
          renderLink={(item, className, content) => (
            <Link
              key={item.key}
              to={item.href}
              className={className}
              title={navCollapsed ? item.label : undefined}
            >
              {content}
            </Link>
          )}
        />
      }
      /*
       * Who is signed in, at the foot of the column.
       *
       * **The same fact was already in the header and this is not a second copy of it** — it is
       * the same fact given the job the header's capsule could not do. That capsule answers "who
       * am I" on hover; this one is a *destination*, and the profile screen behind it is where a
       * person's own details and preferences live. A header control that navigated would have put
       * a page under a hover card.
       *
       * The name degrades exactly as the header's does — name, then email, then a stated unknown —
       * because both read the same `self` and a second fallback order is how the two start
       * disagreeing about what to call somebody.
       */
      navFooter={
        <Link
          to="/profile"
          className="tf-nav__identity"
          aria-label={t('profile.nav.aria', { name: personName(self, t('shell.user.unknown')) })}
        >
          <PersonCapsule name={personName(self, t('shell.user.unknown'))} size="sm" />
        </Link>
      }
      /*
       * D-07 *(revised)*: the region holds the flyout when it is showing and collapses when it is
       * not, so every screen gets the width back the moment nobody is using the assistant. The
       * shell owns the region — FE-04's real point — and the flyout owns what goes in it.
       */
      copilot={
        // `showing && holdsEdge` — the second is the register's answer, not a second open state.
        // A flyout outranks the assistant while it is up, and the assistant is *suppressed* rather
        // than closed, so it returns with its transcript when the act finishes.
        showing && holdsEdge ? (
          <CopilotFlyout
            pinned={pinned}
            onTogglePin={() => setPinned.mutate(!pinned)}
            onClose={() => {
              copilot.close();
              if (pinned) setPinned.mutate(false);
            }}
          />
        ) : null
      }
    >
      <Outlet />
    </AppShell>
  );
}
